'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

export type ServiceBadge = 'HOT' | 'NEW' | 'COMING SOON';

export interface ServiceCardProps {
  name: string;
  image: string;
  href: string;
  badge?: ServiceBadge;
  size?: 'sm' | 'md';
}

const FALLBACK_IMAGE = '/assets/images/services-default.png';

const badgeColors: Record<ServiceBadge, string> = {
  HOT: '#FF3B5C',
  NEW: '#00C853',
  'COMING SOON': '#FFB300',
};

export function ServiceCard({ name, image, href, badge, size = 'md' }: ServiceCardProps) {
  const [imgSrc, setImgSrc] = useState(image);
  const [loaded, setLoaded] = useState(false);

  const imgPx = size === 'sm' ? 40 : 56;
  const cardPad = size === 'sm' ? 'px-2 py-3' : 'px-3 py-4';
  const cardMin = size === 'sm' ? 'min-h-[100px]' : 'min-h-[120px]';
  const textSize = size === 'sm' ? 'text-[9px]' : 'text-[10px]';

  return (
    <Link
      href={href}
      className={`group relative flex flex-col items-center justify-center ${cardMin} ${cardPad} rounded-[18px] transition-all duration-200 hover:-translate-y-1 active:scale-[0.97] hover:bg-[#17233b] active:bg-[#17233b]`}
      style={{
        background: '#0d1829',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      {badge && (
        <span
          className="absolute right-1.5 top-1.5 rounded-full px-1.5 py-0.5 text-[8px] font-bold text-black z-10"
          style={{ background: badgeColors[badge] }}
        >
          {badge}
        </span>
      )}

      {/* Image container with skeleton */}
      <div
        className="relative mb-2 overflow-hidden rounded-2xl flex-shrink-0 flex items-center justify-center"
        style={{
          width: imgPx,
          height: imgPx,
          background: 'rgba(255,255,255,0.04)',
        }}
      >
        {!loaded && (
          <div className="absolute inset-0 rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
        )}
        <Image
          src={imgSrc}
          alt={`${name} service`}
          width={imgPx}
          height={imgPx}
          className={`object-contain p-1 transition-all duration-300 group-hover:scale-110 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setLoaded(true)}
          onError={() => { setImgSrc(FALLBACK_IMAGE); setLoaded(true); }}
        />
      </div>

      <span className={`${textSize} font-medium text-center leading-tight`}
        style={{ color: 'rgba(255,255,255,0.75)' }}>
        {name}
      </span>
    </Link>
  );
}

export default ServiceCard;
